import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, catchError, exhaustMap, filter, firstValueFrom, from, map, of, tap } from 'rxjs';
import { firstFromEnvelope } from '../../envelope';
import { Api as AdministrationApi } from '../../api/generated/administration/api';
import { getUserProfile } from '../../api/generated/administration/fn/users/get-user-profile';
import { GhUserProfileDto } from '../../api/generated/administration/models/gh-user-profile-dto';
import { Api as IdentityApi } from '../../api/generated/identity/api';
import { passwordLogin } from '../../api/generated/identity/fn/auth-controller/password-login';
import { refreshToken } from '../../api/generated/identity/fn/auth-controller/refresh-token';
import { userLogout } from '../../api/generated/identity/fn/auth-controller/user-logout';
import { LoginRequest } from '../../api/generated/identity/models/login-request';
import { TokenResponse } from '../../api/generated/identity/models/token-response';
import { IS_REFRESH_TOKEN } from '../../auth/auth.interceptor';
import { AppError } from '../../models/app-error.model';
import { OfflineDb } from '../../offline/db';
import { TokenStorageService } from '../../auth/token-storage.service';
import { AuthActions } from './auth.actions';
import { UserActions } from '../user/user.actions';

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as AppError)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly identityApi = inject(IdentityApi);
  private readonly administrationApi = inject(AdministrationApi);
  private readonly tokens = inject(TokenStorageService);
  private readonly db = inject(OfflineDb);
  private readonly router = inject(Router);

  readonly initialize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initializeRequested),
      exhaustMap(() =>
        from(
          (async () => {
            const cached = await this.db.userProfile.toCollection().first();
            let accessToken = this.tokens.getAccessToken();
            const restoredFromRefresh = !accessToken && !!this.tokens.getRefreshToken();

            if (restoredFromRefresh) {
              const token = await firstValueFrom(this.refreshSession());
              accessToken = token?.accessToken ?? null;
              if (!accessToken) {
                throw new Error('Stored session could not be restored');
              }
              this.persistTokens(accessToken, token?.refreshToken ?? this.tokens.getRefreshToken());
            }

            if (!accessToken || !restoredFromRefresh) {
              return AuthActions.initializeSucceeded({
                accessToken,
                profile: cached?.profile ?? null,
                profileSyncedAt: cached?.syncedAt ?? null,
              });
            }

            try {
              const profile = await firstValueFrom(this.loadProfile());
              if (!profile) {
                throw new Error('Profile unavailable');
              }
              const profileSyncedAt = await this.cacheProfile(profile);
              return AuthActions.initializeSucceeded({
                accessToken,
                profile,
                profileSyncedAt,
              });
            } catch {
              return AuthActions.initializeSucceeded({
                accessToken,
                profile: cached?.profile ?? null,
                profileSyncedAt: cached?.syncedAt ?? null,
              });
            }
          })(),
        ).pipe(
          catchError((error: unknown) =>
            from(this.clearSession()).pipe(
              map(() =>
                AuthActions.initializeFailed({
                  error: errorMessage(error, 'Session initialization failed'),
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  readonly revalidateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initializeSucceeded),
      filter(({ accessToken }) => !!accessToken),
      map(() => UserActions.loadProfileRequested()),
    ),
  );

  readonly login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginRequested),
      exhaustMap(({ credentials }) =>
        from(
          (async () => {
            const token = await firstValueFrom(this.login(credentials));
            if (!token?.accessToken) {
              throw new Error('Login failed');
            }

            this.persistTokens(token.accessToken, token.refreshToken ?? null);
            const profile = await firstValueFrom(this.loadProfile());
            if (!profile) {
              throw new Error('Profile unavailable');
            }

            const profileSyncedAt = await this.cacheProfile(profile);
            return AuthActions.loginSucceeded({ token, profile, profileSyncedAt });
          })(),
        ).pipe(
          catchError((error: unknown) =>
            from(this.clearSession()).pipe(
              map(() => AuthActions.loginFailed({ error: errorMessage(error, 'Login failed') })),
            ),
          ),
        ),
      ),
    ),
  );

  readonly refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshRequested),
      exhaustMap(() =>
        this.refreshSession().pipe(
          map((token) => {
            if (!token?.accessToken) {
              throw new Error('Session refresh failed');
            }
            this.persistTokens(
              token.accessToken,
              token.refreshToken ?? this.tokens.getRefreshToken(),
            );
            return AuthActions.refreshSucceeded({ token });
          }),
          catchError((error: unknown) =>
            from(this.clearSession()).pipe(
              map(() =>
                AuthActions.refreshFailed({
                  error: errorMessage(error, 'Session refresh failed'),
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  readonly logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutRequested),
      exhaustMap(({ reason }) =>
        this.identityApi.invoke(userLogout, {}).pipe(
          catchError(() => of(undefined)),
          exhaustMap(() => from(this.clearSession())),
          map(() => AuthActions.sessionCleared({ reason })),
        ),
      ),
    ),
  );

  readonly navigateAfterLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSucceeded),
        tap(() => void this.router.navigateByUrl('/app', { replaceUrl: true })),
      ),
    { dispatch: false },
  );

  readonly navigateAfterSessionEnd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionCleared, AuthActions.refreshFailed),
        tap((action) => {
          const reason =
            action.type === AuthActions.refreshFailed.type
              ? 'session-expired'
              : action.reason === 'idle'
                ? 'idle'
                : undefined;
          void this.router.navigate(['/login'], {
            queryParams: reason ? { reason } : undefined,
          });
        }),
      ),
    { dispatch: false },
  );

  private login(credentials: LoginRequest): Observable<TokenResponse | undefined> {
    return this.identityApi
      .invoke(passwordLogin, { body: credentials })
      .pipe(map((body) => firstFromEnvelope(body.data)));
  }

  private refreshSession(): Observable<TokenResponse | undefined> {
    return this.identityApi
      .invoke(refreshToken, {}, new HttpContext().set(IS_REFRESH_TOKEN, true))
      .pipe(map((body) => firstFromEnvelope(body.data)));
  }

  private loadProfile(): Observable<GhUserProfileDto | undefined> {
    return this.administrationApi
      .invoke(getUserProfile, {})
      .pipe(map((body) => firstFromEnvelope(body.data)));
  }

  private persistTokens(accessToken: string, refreshTokenValue: string | null): void {
    this.tokens.setAccessToken(accessToken);
    this.tokens.setRefreshToken(refreshTokenValue);
  }

  private async cacheProfile(profile: GhUserProfileDto): Promise<string> {
    const syncedAt = new Date().toISOString();
    await this.db.userProfile.clear();
    await this.db.userProfile.put({ id: 'current', profile, syncedAt });
    return syncedAt;
  }

  private async clearSession(): Promise<void> {
    this.tokens.clear();
    await this.db.userProfile.clear();
  }
}
